import { ArticleList } from '@sprinx-app/ui-commerce';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Breadcrumb from '@/components/breadcrumb';
import LayoutBase from '@/components/layout-base';
import PageHeadingWithNavigation from '@/components/page-heading-with-navigation';
import contentLayoutBaseData from '@/content/content-layout-base-data';
import contentNewsDetailUrl from '@/content/content-news-detail-url';
import type { NewsArticleTag, NewsListItem } from '@/content/content-news-list-data';
import { contentNewListData } from '@/content/content-news-list-data';
import contentNewsListUrl from '@/content/content-news-list-url';
import i18nMessagesLoad from '@/lib/i18n-messages-load';
import { localizationContextFromGetStaticPropsContext } from '@/lib/localization-contenxt';
import revalidateAfter from '@/lib/revalidate-after';
import articleListClasses from '../../styles/modules/article-list.module.css';
import type { NextGetStaticProps, NextPageWithLayout } from '../_app';

type NewsListPageUrlParams = { tag?: NewsArticleTag };

export type NewsListPageProps = {
  articles: NewsListItem[];
  navigation: { code: string; title: string }[];
};

const NewsListPage: NextPageWithLayout<NewsListPageProps> = ({ articles, navigation }) => {
  const intl = useIntl();
  const router = useRouter();

  const { tag: tagQueryParam } = router.query as NewsListPageUrlParams;
  const tag = tagQueryParam == null ? null : (decodeURIComponent(tagQueryParam) as NewsArticleTag);

  const pageTitle = useMemo(
    () =>
      intl.formatMessage({
        defaultMessage: 'Zprávy',
        description: 'Title of page News.',
        id: 'c733KF',
      }),
    [intl],
  );

  return (
    <>
      <NextSeo title={pageTitle} />

      <main className='content-container mb-8'>
        <Breadcrumb className='py-4'>
          <Breadcrumb.Item href={contentNewsListUrl()} isCurrenctPage={true} position={1}>
            {pageTitle}
          </Breadcrumb.Item>
        </Breadcrumb>

        <PageHeadingWithNavigation>
          <PageHeadingWithNavigation.Heading className='heading-page flex-1'>
            {pageTitle}
          </PageHeadingWithNavigation.Heading>
          <PageHeadingWithNavigation.Navigation>
            <PageHeadingWithNavigation.Navigation.Item
              className='text-primary hover:text-primary-focus'
              href={''}
              isActive={tag == null}
            >
              {'Vše'}
            </PageHeadingWithNavigation.Navigation.Item>
            {navigation.map((n) => (
              <PageHeadingWithNavigation.Navigation.Item
                key={n.code}
                className='text-primary hover:text-primary-focus'
                href={`?tag=${encodeURIComponent(n.code)}`}
                isActive={tag == n.code}
              >
                {n.title}
              </PageHeadingWithNavigation.Navigation.Item>
            ))}
          </PageHeadingWithNavigation.Navigation>
        </PageHeadingWithNavigation>

        <ArticleList className={articleListClasses.root}>
          {articles
            .filter((a) => (tag == null ? true : a.categories == null ? false : a.categories.includes(tag)))
            .map((article) => (
              <ArticleList.Item
                key={article.path}
                className={articleListClasses.item}
                href={contentNewsDetailUrl(article.slug)}
              >
                <ArticleList.Item.Image
                  alt={article.title}
                  className={articleListClasses.itemImage}
                  src={article.teaserImage}
                />
                <ArticleList.Item.Heading className={articleListClasses.itemHeading}>
                  {article.title}
                </ArticleList.Item.Heading>
                <ArticleList.Item.Text className={articleListClasses.itemText}>
                  {article.teaserText}
                </ArticleList.Item.Text>
              </ArticleList.Item>
            ))}
        </ArticleList>
      </main>
    </>
  );
};

NewsListPage.displayName = 'NewsListPage';

NewsListPage.getLayout = (page) => {
  return <LayoutBase>{page}</LayoutBase>;
};

export default NewsListPage;

// *****************************************
// *
// * getStaticProps
// *
// *****************************************

export const getStaticProps: NextGetStaticProps<NewsListPageProps, NewsListPageUrlParams> = async (
  getStaticPropsContext,
) => {
  const localizationContext = localizationContextFromGetStaticPropsContext(getStaticPropsContext);
  const intlMessages = await i18nMessagesLoad(localizationContext);
  const layoutData = await contentLayoutBaseData(localizationContext);

  const newsListData = await contentNewListData(localizationContext);

  return {
    props: {
      ...newsListData,
      intlMessages,
      layoutData,
    },
    revalidate: revalidateAfter('1h'),
  };
};
